// <heimdall-demo demo="abc123"> custom element. Tiny self-contained
// bundle (no Preact import) loaded by host marketing pages via:
//
//   <heimdall-demo demo="abc123"></heimdall-demo>
//   <script async src="https://app.heimdallpresent.local/player/heimdall-demo.js"></script>
//
// The element creates a shadow root, mounts an inline poster iframe at
// `?mode=embed` (which the player runtime renders as the EmbedShell —
// poster + Click for interactive demo pill), and listens for
// postMessages from the iframe. On `heimdall:open` it lazy-mounts a
// host-page-level modal overlay containing a SECOND iframe pointing at
// `/play/<slug>` (no embed mode) — the full player runs there. Closing
// (X click or backdrop click) tears the modal down.
//
// Mode:
//   - default ("modal"): the open behaviour above. Poster + host-modal.
//   - inline (`<heimdall-demo demo="..." inline>`): no modal — the
//     iframe goes fullscreen via the EmbedShell's existing fullscreen
//     API behaviour. Used when the host page's chrome should remain
//     visible / when fullscreen is preferable to a host-modal.
//
// Host attribute:
//   - host (`<heimdall-demo host="https://other-app.com">`) overrides
//     the default origin (the script URL's origin). Useful when
//     CDN-hosted scripts point at a different player origin.

const DEFAULT_HOST = scriptOrigin();

class HeimdallDemoElement extends HTMLElement {
  private posterIframe: HTMLIFrameElement | null = null;
  private modalRoot: HTMLDivElement | null = null;
  private listener: ((e: MessageEvent) => void) | null = null;

  connectedCallback() {
    if (this.shadowRoot) return; // already connected
    const shadow = this.attachShadow({ mode: "open" });
    const style = makeStyle();
    shadow.appendChild(style);

    const slug = this.getAttribute("demo");
    if (!slug) {
      shadow.appendChild(textNode("missing demo attribute"));
      return;
    }

    this.posterIframe = makeIframe(this.posterUrl(slug));
    shadow.appendChild(this.posterIframe);

    this.listener = (e) => this.onMessage(e);
    window.addEventListener("message", this.listener);
  }

  disconnectedCallback() {
    if (this.listener) window.removeEventListener("message", this.listener);
    this.listener = null;
    this.tearDownModal();
  }

  // Public API --------------------------------------------------------

  next() {
    // Phase 1 doesn't expose programmatic next; reserved for Phase 4.
  }

  back() {
    this.postToActiveIframe({ type: "heimdall:goto" });
  }

  restart() {
    this.postToActiveIframe({ type: "heimdall:restart" });
  }

  setVariable(_key: string, _value: string) {
    // Phase 4 stub.
  }

  // Internals ---------------------------------------------------------

  private get host(): string {
    return this.getAttribute("host") || DEFAULT_HOST;
  }

  private get isInline(): boolean {
    return this.hasAttribute("inline");
  }

  private posterUrl(slug: string): string {
    return `${this.host}/play/${encodeURIComponent(slug)}?mode=embed`;
  }

  private modalUrl(slug: string): string {
    return `${this.host}/play/${encodeURIComponent(slug)}`;
  }

  private onMessage(e: MessageEvent) {
    const data = e.data;
    if (!data || typeof data !== "object") return;
    const type = (data as { type?: string }).type;

    if (type === "heimdall:open" && !this.isInline) {
      this.openModal();
      this.dispatchEvent(new CustomEvent("heimdall:open"));
    } else if (type === "heimdall:close") {
      this.tearDownModal();
      this.dispatchEvent(new CustomEvent("heimdall:close"));
    } else if (type === "heimdall:step_seen") {
      this.dispatchEvent(new CustomEvent("heimdall:step_seen", { detail: data }));
    } else if (type === "heimdall:completed") {
      this.dispatchEvent(new CustomEvent("heimdall:completed", { detail: data }));
    } else if (type === "heimdall:resize") {
      const height = (data as { height?: number }).height;
      if (this.posterIframe && typeof height === "number") {
        this.posterIframe.style.height = `${height}px`;
      }
    }
  }

  private openModal() {
    if (this.modalRoot) return;
    const slug = this.getAttribute("demo");
    if (!slug) return;

    const root = document.createElement("div");
    root.className = "heimdall-modal-root";
    Object.assign(root.style, modalRootStyle);

    const backdrop = document.createElement("div");
    Object.assign(backdrop.style, modalBackdropStyle);
    backdrop.addEventListener("click", () => this.tearDownModal());

    const frame = makeIframe(this.modalUrl(slug));
    Object.assign(frame.style, modalFrameStyle);

    const close = document.createElement("button");
    close.setAttribute("aria-label", "Close demo");
    close.innerHTML =
      '<svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"/></svg>';
    Object.assign(close.style, modalCloseStyle);
    close.addEventListener("click", () => this.tearDownModal());

    root.appendChild(backdrop);
    root.appendChild(frame);
    root.appendChild(close);
    document.body.appendChild(root);
    document.body.style.overflow = "hidden";
    this.modalRoot = root;

    // Esc to close.
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") this.tearDownModal();
    };
    window.addEventListener("keydown", onKey);
    (root as HTMLDivElement & { __escListener?: () => void }).__escListener = () => {
      window.removeEventListener("keydown", onKey);
    };
  }

  private tearDownModal() {
    if (!this.modalRoot) return;
    const cleanup = (this.modalRoot as HTMLDivElement & { __escListener?: () => void })
      .__escListener;
    if (cleanup) cleanup();
    this.modalRoot.remove();
    this.modalRoot = null;
    document.body.style.overflow = "";
  }

  private postToActiveIframe(message: unknown) {
    const target = this.modalRoot?.querySelector("iframe") ?? this.posterIframe;
    if (!target) return;
    try {
      target.contentWindow?.postMessage(message, "*");
    } catch {
      // Cross-origin guard; silent.
    }
  }
}

function makeIframe(src: string): HTMLIFrameElement {
  const frame = document.createElement("iframe");
  frame.src = src;
  frame.loading = "lazy";
  frame.allow = "fullscreen";
  frame.setAttribute("allowfullscreen", "");
  frame.style.width = "100%";
  frame.style.height = "100%";
  frame.style.border = "0";
  frame.style.borderRadius = "12px";
  frame.style.background = "transparent";
  return frame;
}

function makeStyle(): HTMLStyleElement {
  const style = document.createElement("style");
  style.textContent = `
    :host {
      display: block;
      width: 100%;
      aspect-ratio: 16 / 9;
      position: relative;
    }
    iframe {
      width: 100%;
      height: 100%;
      display: block;
    }
  `;
  return style;
}

function textNode(text: string): HTMLDivElement {
  const div = document.createElement("div");
  div.textContent = text;
  div.style.color = "#a3b1c6";
  div.style.padding = "1rem";
  return div;
}

function scriptOrigin(): string {
  // The script's own URL — works because this file is loaded via a
  // <script src="https://.../player/heimdall-demo.js"> tag, and
  // document.currentScript points at it during top-level execution.
  const me = document.currentScript as HTMLScriptElement | null;
  if (me?.src) {
    try {
      return new URL(me.src).origin;
    } catch {
      // fall through
    }
  }
  return window.location.origin;
}

const modalRootStyle: Partial<CSSStyleDeclaration> = {
  position: "fixed",
  inset: "0",
  zIndex: "2147483647", // top of the host page
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: "24px",
};

const modalBackdropStyle: Partial<CSSStyleDeclaration> = {
  position: "absolute",
  inset: "0",
  background: "rgba(11, 13, 18, 0.78)",
  backdropFilter: "blur(2px)",
  cursor: "pointer",
};

const modalFrameStyle: Partial<CSSStyleDeclaration> = {
  position: "relative",
  width: "min(96vw, 1400px)",
  height: "min(92vh, 900px)",
  maxHeight: "92vh",
  border: "0",
  borderRadius: "16px",
  background: "#0b0d12",
  boxShadow: "0 30px 100px rgba(0, 0, 0, 0.5)",
};

const modalCloseStyle: Partial<CSSStyleDeclaration> = {
  position: "absolute",
  top: "max(20px, env(safe-area-inset-top))",
  right: "max(20px, env(safe-area-inset-right))",
  width: "44px",
  height: "44px",
  borderRadius: "50%",
  border: "1px solid rgba(255, 255, 255, 0.18)",
  background: "rgba(11, 13, 18, 0.85)",
  color: "white",
  cursor: "pointer",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: "1",
};

if (typeof customElements !== "undefined" && !customElements.get("heimdall-demo")) {
  customElements.define("heimdall-demo", HeimdallDemoElement);
}
